/**
 * MarkdownEditor — F12.B T12.B.3.
 * Textarea po lewej + podgląd HTML po prawej (split view).
 * Używa biblioteki `marked` do konwersji Markdown → HTML.
 */
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Eye, EyeOff } from 'lucide-react';
import { marked } from 'marked';

interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  rows?: number;
}

marked.setOptions({ breaks: true });

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Wpisz tekst w formacie Markdown…',
  rows = 8,
}: MarkdownEditorProps) {
  const [showPreview, setShowPreview] = useState(false);

  // marked NIE usuwa surowego HTML z markdownu (np. `<img onerror=…>`), więc sanityzujemy
  // wyjście DOMPurify przed wstrzyknięciem — nawet dla treści „własnej" (komponent współdzielony).
  const rendered = DOMPurify.sanitize(marked.parse(value || '') as string);

  return (
    <div className="rounded-md border border-border-primary bg-bg-primary focus-within:border-accent-500 focus-within:ring-1 focus-within:ring-accent-500">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border-primary px-3 py-1.5">
        <span className="text-xs font-medium text-text-muted">Markdown</span>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
        >
          {showPreview ? (
            <>
              <EyeOff className="h-3.5 w-3.5" />
              Edytor
            </>
          ) : (
            <>
              <Eye className="h-3.5 w-3.5" />
              Podgląd
            </>
          )}
        </button>
      </div>

      {showPreview ? (
        <div
          className="prose prose-sm max-w-none px-3 py-2 text-sm text-text-primary"
          // rendered = wyjście marked przepuszczone przez DOMPurify (patrz wyżej)
          dangerouslySetInnerHTML={{ __html: rendered }}
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full resize-y bg-transparent px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
      )}
    </div>
  );
}
